<template>
  <div>
    <h3 class="ui dividing header">
      <i class="calendar alternate outline icon"/>
      <div class="content">
        {{ $t('patientInfo.monitoring' ) }}
        <div class="sub header">
          {{ $t('patientInfo.monitoringSub') }}
        </div>
      </div>
    </h3>

    <div
      v-if="!patient.consent"
      class="ui info message">
      <i class="user icon"/>
      {{ $t('patientInfo.noConsent') }}
    </div>

    <template v-else>
      <div style="margin-bottom: 1em;">
        <div
          class="ui primary button"
          @click.prevent="generateReportsOrCopyLink('en')"
          :class="{ disabled: generatingPDF}">
          <span v-if="language===null || language==='de'"><i class="download icon"/> {{ $t('patientInfo.generate', { report: $t('patientInfo.impactReports') }) }}  (EN)</span>
          <span v-if="language==='en' && generatingPDF===true"><i class="download icon"/> {{ $t('patientInfo.generating', { report: $t('patientInfo.impactReports') }) }}  (EN)</span>
          <span v-if="language==='en' && generatingPDF===false"><i class="download icon"/> {{ $t('patientInfo.download', { report: $t('patientInfo.impactReports') }) }}  (EN)</span>
        </div>
        <div
          class="ui basic primary button"
          @click.prevent="generateReportsOrCopyLink('de')"
          :class="{ disabled: generatingPDF}">
          <span v-if="language===null || language==='en'"><i class="download icon"/> {{ $t('patientInfo.generate', { report: $t('patientInfo.impactReports') }) }}  (DE)</span>
          <span v-if="language==='de' && generatingPDF===true"><i class="download icon"/> {{ $t('patientInfo.generating', { report: $t('patientInfo.impactReports') }) }}  (DE)</span>
          <span v-if="language==='de' && generatingPDF===false"><i class="download icon"/> {{ $t('patientInfo.download', { report: $t('patientInfo.impactReports') }) }}  (DE)</span>
        </div>
      </div>

      <div class="ui fluid styled accordion">
        <div class="active title">
          <h3 class="ui header">
            <i class="dropdown icon"/>
            {{ $t('patientInfo.dailyActionsMigStatus') }}
          </h3>
        </div>
        <div class="active content">
          <table
            class="ui very basic unstackable table">
            <thead>
              <tr class="center aligned">
                <th class="one wide">
                  {{ $t('programDay') }}
                </th>
                <th class="one wide">
                  {{ $t('patientInfo.migraineFree') }}
                </th>
                <th>
                  <div class="ui grid">
                    <div class="four wide column left aligned">
                      {{ $tc('actions.shortName', 2) }}
                    </div>
                    <div class="twelve wide column right aligned">
                      {{ $t('legend') }}:&nbsp;
                      <span class="ui green compact icon label">
                        <i class="checkmark icon"/>
                        {{ $t('actions.completed') }}
                      </span>
                      <span class="ui red compact icon label">
                        <i class="warning icon"/>
                        {{ $t('actions.incomplete') }}
                      </span>
                      <span class="ui compact icon label">
                        <i class="question icon"/>
                        {{ $t('actions.notFilled') }}
                      </span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(day, index) in dailyArray"
                :key="`day-${index + 1}`">
                <td class="center aligned">
                  {{ index + 1 }}
                </td>
                <td
                  v-if="migraineDaysArray.indexOf(day.timestamp) === -1"
                  class="positive center aligned">
                  <i class="green checkmark icon"/>
                </td>
                <td
                  v-else
                  class="negative center aligned">
                  <i class="red close icon"/>
                </td>
                <td>
                  <template v-for="(item, index) in getDailyActions(day.timestamp)">
                    <span
                      v-if="item.data.completionStatus === true"
                      :key="`daily-action-${index}`"
                      class="ui green compact icon label">
                      <i class="checkmark icon"/>
                      {{ getActionShortName(item.data.actionId) }}
                    </span>
                    <span
                      v-else-if="item.data.completionStatus === false"
                      :key="`daily-action-${index}`"
                      class="ui red compact icon label">
                      <i class="exclamation icon"/>
                      {{ getActionShortName(item.data.actionId) }}
                    </span>
                    <span
                      v-else
                      :key="`daily-action-${index}`"
                      class="ui compact icon label">
                      <i class="question icon"/>
                      {{ getActionShortName(item.data.actionId) }}
                    </span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="title">
          <h3 class="ui header">
            <i class="dropdown icon"/>
            {{ $t('patientInfo.oneTimeActions') }}
          </h3>
        </div>
        <div class="content">
          <table
            class="ui very basic table">
            <thead>
              <tr>
                <th>
                  {{ $tc('actions.shortName') }}
                </th>
                <th>
                  {{ $t('actions.dueDate') }}
                </th>
                <th>
                  {{ $t('actions.completionStatus') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in oneTimeEvents"
                :key="item.id">
                <td>
                  {{ getActionShortName(item.data.actionId) }}
                </td>
                <td>
                  {{ $d(new Date(item.endTimestamp), 'short') }}
                </td>
                <td>
                  <i
                    v-if="item.data.completionStatus === true"
                    class="green checkmark icon"/>
                  <i
                    v-if="item.data.completionStatus === false"
                    class="red close icon"/>
                  <i
                    v-else
                    class="question icon"/>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <generate-p-d-f-modal
      @modal-id="setModalId"
      @error-modal-id="setErrorModalId"
      @retry-generate-mir="generateMIR"
      :generating-p-d-f="generatingPDF"
      :pdf-short-link="pdfShortLink"
      :pdf-long-link="pdfLongLink"
      :language="language"/>
  </div>
</template>

<script>

import GeneratePDFModal from './GeneratePDFModal'
export default {
  name: 'PatientMonitoring',
  components: { GeneratePDFModal },
  props: {
    patient: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      migraineDaysArray: [],
      generatingPDF: null,
      pdfShortLink: null,
      pdfLongLink: null,
      language: null,
      modalId: null,
      errorModalId: null,
      timeout: 20000
    }
  },
  computed: {
    actionEvents () {
      return this.patient.userEvents.filter(event => event.type === 'action')
    },
    oneTimeEvents () {
      return this.actionEvents.filter(event => event.data.trackingType === 'once')
    },
    dailyTrackingEvents () {
      return this.actionEvents.filter(event => event.data.trackingType === 'daily')
    },
    dailyArray () {
      let output = []

      let startDate = this.patient.data.startDate

      if (this.patient.data.startDate === null) {
        return output
      }

      let currentDay = Math.floor((Date.now() - startDate) / 86400000) + 1

      for (let i = 0; i < currentDay; i++) {
        output.push({ timestamp: startDate + (86400000 * i) })
      }

      return output
    },
    reportTimePeriod: function () {
      let now = new Date()
      let startTime = this.patient.data.startDate
      let endTime = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())

      return {
        startTime: startTime,
        endTime: endTime
      }
    },
    reportRequestURL: function () {
      return process.env.VUE_APP_PROGRAM_URL + '/patients/' + this.patient.userId + '/migraineDays?startTime=' + this.reportTimePeriod.startTime + '&endTime=' + this.reportTimePeriod.endTime
    }
  },
  mounted () {
    $('.ui.accordion').accordion()

    this.getMigraineDays()
  },
  methods: {
    getMigraineDays () {
      this.$axios.get(
        this.reportRequestURL,
        this.requestConfig
      ).then(
        ({ data }) => {
          let allDailyEvents = data.result.dailyEventsData
          let allMigDays = []

          if (allDailyEvents.hasOwnProperty('mig')) {
            Object.keys(allDailyEvents.mig).forEach((subtype) => {
              Object.keys(allDailyEvents.mig[subtype]).forEach((month) => {
                allDailyEvents.mig[subtype][month].forEach((UTCtimestamp) => {
                  allMigDays.push(UTCtimestamp)
                })
              })
            })
          }
          this.migraineDaysArray = [...allMigDays]
        },
        ({ response }) => {
          if (response.status !== 401) {
            this.$rollbar.error('Error in fetching Migraine Days', response)
          }
        }
      )
    },
    findAction (actionId) {
      return this.patient.userPlans.find(userPlan => userPlan.data.id === actionId)
    },
    getActionShortName (actionId) {
      let action = this.findAction(actionId)
      return action.data.title
    },
    getDailyActions (timestamp) {
      return (
        this.dailyTrackingEvents
          .filter(event => event.startTimestamp === timestamp)
      )
    },
    generateReportsOrCopyLink: function (language) {
      if (this.language === language) {
        if (this.generatingPDF === null) {
          this.generateMIR()
        } else if (this.generatingPDF === false) {
          this.showModal()
        }
      } else if (this.language !== language) {
        this.language = language
        this.resetPDFGeneration()
        this.generateMIR()
      }
    },
    resetPDFGeneration: function () {
      this.pdfShortLink = null
      this.pdfLongLink = null
      this.generatingPDF = null
    },
    setModalId: function (modalId) {
      this.modalId = modalId
    },
    setErrorModalId: function (errorModalId) {
      this.errorModalId = errorModalId
    },
    showModal: function () {
      $('#' + this.modalId)
        .modal({ closable: false, autofocus: false, observeChanges: true, transition: 'fade' })
        .modal('show')
    },
    showErrorModal: function () {
      $('#' + this.errorModalId)
        .modal({ closable: false, autofocus: false, observeChanges: true, transition: 'fade' })
        .modal('show')
    },
    hideModal: function () {
      $('#' + this.modalId).modal('hide')
    },
    generateMIR () {
      return new Promise(
        (resolve, reject) => {
          this.generatingPDF = true
          this.showModal()

          const requestUrl = process.env.VUE_APP_PROGRAM_URL + '/hcp-pdf/' + this.patient.userId + '?waitForElement=false'

          const now = new Date()
          const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
          const programEndDate = this.patient.data.endDate
          const startTime = this.patient.data.startDate
          const programEndDateIsValid = programEndDate > startTime

          const endTime = (
            (!programEndDate || !programEndDateIsValid)
              ? today
              : (
                /*
                - If date now is smaller than or equal to program end date, then use date now
                - If date now is bigger than program end date, use program end date
                */
                today <= programEndDate
                  ? today
                  : programEndDate
              )
          )

          const country = 'CH'
          const reportURL = process.env.VUE_APP_PRINT_URL + '?coachprogramid=' + process.env.VUE_APP_PROGRAM_ID + '&userAuthToken=' + encodeURIComponent(this.userAuthToken) + '&uid=' + this.patient.userId + '&startTime=' + startTime + '&endTime=' + endTime + '&country=' + country + '&lang=' + this.language + '&mode=coach#/print'

          this.$axios.get(
            requestUrl,
            {
              headers: {
                'Content-type': 'application/json',
                'accept': 'application/json',
                'x-auth-token': this.userAuthToken,
                'report-url': reportURL // FIXME: This will be the production MIR link of Migraine Impact Reports
              },
              timeout: this.timeout
            }
          ).then(
            ({ data }) => {
              this.pdfLongLink = data.result.longUrl
              this.pdfShortLink = data.result.shortUrl
              this.generatingPDF = false

              resolve()
            },
            ({ response }) => {
              setTimeout(
                () => {
                  this.hideModal()
                },
                1000
              )
              setTimeout(
                () => {
                  this.resetPDFGeneration()
                },
                2000
              )
              setTimeout(
                () => {
                  this.$rollbar.error(`Failed to generate MIR for \`userId\`: ${this.patient.userId}`, response)
                  this.showErrorModal()
                  reject(response)
                },
                2000
              )
            }
          )
        }
      ).catch(
        error => {
          this.$rollbar.error(`Request to generate MIR for user hit ${this.timeout} milliseconds timeout`, error)
        }
      )
    }
  }
}
</script>

<style scoped>
/*
.ui.styled.accordion .content {
  padding: 1em;
  padding-left: 2em; */
/* } */
</style>
